/**
 * Chat Widgets service. Types derive from REAL captured staging responses in
 * docs/widgets-migration/Api-response/ — see its README for the verified quirks.
 *
 * Unlike every other service here this one does NOT talk to chat-api: it takes a
 * fully-formed URL derived from the widget extension's adminURL, and authenticates
 * with the appToken already embedded in it, so getChatApiBase/getChatApiHeaders don't apply.
 */
import axios from './axiosInstance'

/** Extension whose adminURL the widget API base is derived from. */
export const WIDGET_EXTENSION_ID = 'widget'

/** Only v2 widgets are editable; v1 records stay in the combined list. */
export const WIDGET_CONFIG_VERSION_V2 = 'v2'

/** Error envelope. Capture: error-auth.json. `source` is "ext-api", not "chat-api". */
export interface WidgetApiErrorBody {
  code: string
  message: string
  devMessage?: string
  source?: string
  details?: Record<string, string[] | string>
}

export interface WidgetApiError {
  error: WidgetApiErrorBody
}

/**
 * Quirk (verified): style/sidebar/main are all optional. Partial records exist on
 * real apps — a PUT with an unknown widgetId upserts one holding only name/version/
 * widgetId, and it still passes the v2 list filter.
 */
export interface WidgetConfiguration {
  version?: string
  name?: string
  /** Present only on widgets that have been saved at least once — PUT writes the id into the config. */
  widgetId?: string
  style?: WidgetStyleConfig
  sidebar?: WidgetSidebarConfig
  main?: WidgetMainConfig
  [key: string]: unknown
}

/** 9 keys — the Customize screen. Capture: widgets-list.json. */
export interface WidgetStyleConfig {
  primary_color?: string
  foreground_color?: string
  background_color?: string
  override_system_background_colors?: boolean
  custom_js?: string
  custom_css?: string
  docked_layout_icon_open?: string
  docked_layout_icon_close?: string
  docked_layout_icon_background?: string
}

/** 10 keys — the Settings > Sidebar tab. */
export interface WidgetSidebarConfig {
  recent_chat_listing?: string
  user_listing?: string
  group_listing?: string
  chats?: boolean
  users?: boolean
  groups?: boolean
  calls?: boolean
  user_settings?: boolean
  sidebar_navigation_sequence?: string[]
  start_a_new_conversation?: string
  [key: string]: unknown
}

/**
 * The 46 `main` keys, in the order the API returns them. The update payload
 * replaces `main` wholesale, so every key must be listed or it would be dropped
 * from the stored configuration.
 */
export const MAIN_SETTING_KEYS = [
  'enable_sending_messages',
  'allow_mention_members',
  'enable_editing_messages',
  'enable_deleting_messages',
  'hide_deleted_messages',
  'enable_replying_to_messages',
  'enable_threaded_replies',
  'enable_share_copy_forward_messages',
  'highlight_messages_from_moderators',
  'enable_voice_calling',
  'enable_video_calling',
  'show_call_recording_option',
  'send_photos_videos',
  'send_files',
  'send_voice_notes',
  'send_emojis',
  'show_emojis_in_larger_size',
  'send_gifs',
  'share_live_reactions',
  'share_location',
  'set_groups_in_qna_mode_by_moderators',
  'create_groups',
  'allow_add_members',
  'join_or_leave_groups',
  'hide_join_leave_notifications',
  'allow_delete_groups',
  'view_group_members',
  'view_shared_media',
  'send_message_in_private_to_group_member',
  'show_typing_indicators',
  'show_delivery_read_indicators',
  'show_user_presence',
  'show_call_notifications',
  'block_user',
  'allow_moderator_to_delete_member_messages',
  'allow_kick_ban_members',
  'allow_promote_demote_members',
  'allow_creating_polls',
  'enable_sound_for_calls',
  'enable_sound_for_messages',
  'show_stickers',
  'allow_message_reactions',
  'enable_collaborative_document',
  'enable_collaborative_whiteboard',
  'enable_message_translation',
  'send_reply_in_private_to_group_member',
] as const

export type WidgetMainKey = (typeof MAIN_SETTING_KEYS)[number]

/** The Settings > Chat Component tab. Keyed so an unknown field is a compile error. */
export type WidgetMainConfig = Partial<Record<WidgetMainKey, boolean>>

export interface ChatWidget {
  widgetId: string
  configuration: WidgetConfiguration
}

/** Shape of an entry in apps.extensionsInstalledList that we care about. */
export interface InstalledExtension {
  id?: string
  adminURL?: string
  [key: string]: unknown
}

export interface WidgetsListResponse {
  data?: ChatWidget[]
  error?: WidgetApiErrorBody
}

/** POST returns the new id only — never the record. Capture: widget-add.json. */
export interface WidgetAddResponse {
  data?: { widgetId: string | null }
  error?: WidgetApiErrorBody
}

/** PUT returns a bare success flag — no data, no record. Capture: widget-update.json. */
export interface WidgetUpdateResponse {
  success?: boolean
  error?: WidgetApiErrorBody
}

export interface WidgetAddPayload {
  ids: string[]
  settings: WidgetConfiguration
}

export interface WidgetUpdatePayload {
  settings: WidgetConfiguration
}

const JSON_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
} as const

/**
 * Returned when the request never reached the API, so callers get the same
 * envelope shape they get from a real API failure instead of a thrown error.
 */
const networkError = (): WidgetApiError => ({
  error: {
    code: 'ERR_NETWORK',
    message: 'Something went wrong. Please try refreshing the page',
    source: 'dashboard',
  },
})

/**
 * NEVER REJECTS (deliberate): the frozen apps/* widget sagas have no try/catch,
 * so a rejection there would kill the saga worker. Failures resolve as an error
 * envelope; callers must test the response shape, never its truthiness — see
 * isWidgetApiError. Normalize to rejection only when those consumers retire.
 */
const resolveError = (error: {
  response?: { data?: unknown }
}): WidgetApiError | Record<string, unknown> => {
  const body = error?.response?.data
  // A gateway can answer with HTML (verified: DELETE returns a 502 page), which
  // is not a usable envelope — treat anything non-object as a network failure.
  if (body && typeof body === 'object') return body as Record<string, unknown>
  return networkError()
}

/** True when a response is an API failure rather than a result. */
export function isWidgetApiError(
  response: unknown,
): response is WidgetApiError {
  return Boolean(
    response &&
      typeof response === 'object' &&
      typeof (response as WidgetApiError).error === 'object' &&
      (response as WidgetApiError).error !== null,
  )
}

/**
 * Derives the widget API base from the installed-extensions list.
 *
 * The extension's adminURL points at the v1 "show-setting" endpoint; the dashboard
 * has always rewritten it to the v2 settings endpoint. Ported verbatim from
 * redux/apps/sagas.js — String.replace swaps the FIRST occurrence only, which is
 * load-bearing: a broader replace would rewrite a host that contains "v1".
 */
export function getWidgetSettingsUrl(
  extensionsInstalledList: InstalledExtension[] | undefined | null,
): string | false {
  if (!Array.isArray(extensionsInstalledList) || !extensionsInstalledList.length) return false
  const widget = extensionsInstalledList.find(ext => ext?.id === WIDGET_EXTENSION_ID)
  const adminURL = widget?.adminURL
  if (!adminURL) return false
  return adminURL.replace('show-setting', 'settings').replace('v1', 'v2')
}

/**
 * The unused `app` parameter is kept in every signature on purpose: the frozen
 * apps/* sagas call these positionally (`list(app, url)`, `add(post, app, url)`)
 * and must keep working untouched. The widget API takes no app credentials — the
 * appToken is already embedded in the derived URL.
 */
export type WidgetServiceApp = unknown

/** Fetches every widget on the app — v1 and v2 alike; filtering is the caller's job. */
export async function list(
  app: WidgetServiceApp,
  widgetSettingsURL = '',
): Promise<WidgetsListResponse> {
  return axios
    .get(widgetSettingsURL, { headers: JSON_HEADERS })
    .then(res => res.data)
    .catch(resolveError) as Promise<WidgetsListResponse>
}

/** Creates a widget. Resolves with the new id only — re-fetch the list for the record. */
export async function add(
  post: WidgetAddPayload,
  app: WidgetServiceApp,
  widgetSettingsURL = '',
): Promise<WidgetAddResponse> {
  return axios
    .post(widgetSettingsURL, post, { headers: JSON_HEADERS })
    .then(res => res.data)
    .catch(resolveError) as Promise<WidgetAddResponse>
}

/**
 * Saves a widget. Note the API treats this as an UPSERT — an unknown widgetId
 * creates a partial record rather than failing, so only ever send an id the list
 * returned.
 */
export async function update(
  post: WidgetUpdatePayload,
  app: WidgetServiceApp,
  widgetSettingsURL = '',
): Promise<WidgetUpdateResponse> {
  return axios
    .put(widgetSettingsURL, post, { headers: JSON_HEADERS })
    .then(res => res.data)
    .catch(resolveError) as Promise<WidgetUpdateResponse>
}
